import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TicketTiers = ({ tickets, onAddToCart, cartItems }) => {
  const getCartQuantity = (ticketId) => {
    const cartItem = cartItems?.find(item => item?.id === ticketId && item?.type === 'ticket');
    return cartItem ? cartItem?.quantity : 0;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(price);
  };

  const getAvailabilityStatus = (available, sold) => {
    const remaining = available - sold;
    if (remaining === 0) return { status: 'sold-out', text: 'Sold Out', color: 'text-error' };
    if (remaining <= 10) return { status: 'low', text: `Only ${remaining} left`, color: 'text-warning' };
    return { status: 'available', text: `${remaining} available`, color: 'text-success' };
  };

  return (
    <div className="bg-card rounded-lg p-6 mb-8">
      <h2 className="text-2xl font-semibold text-foreground mb-6">Select Tickets</h2>
      <div className="space-y-4">
        {tickets?.map((ticket) => {
          const availability = getAvailabilityStatus(ticket?.available, ticket?.sold);
          const cartQuantity = getCartQuantity(ticket?.id);
          const isDisabled = availability?.status === 'sold-out';
          
          return (
            <div
              key={ticket?.id}
              className={`border rounded-lg p-4 transition-colors ${
                isDisabled 
                  ? 'border-border bg-muted/50' :'border-border hover:border-primary/50 bg-background'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className={`text-lg font-medium ${
                      isDisabled ? 'text-muted-foreground' : 'text-foreground'
                    }`}>
                      {ticket?.name}
                    </h3>
                    
                    {ticket?.isPopular && (
                      <span className="px-2 py-1 bg-accent text-accent-foreground text-xs font-medium rounded-full">
                        Popular
                      </span>
                    )}
                  </div>
                  
                  <p className={`text-sm mb-2 ${
                    isDisabled ? 'text-muted-foreground' : 'text-muted-foreground'
                  }`}>
                    {ticket?.description}
                  </p>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <span className={`font-medium ${availability?.color}`}>
                      {availability?.text}
                    </span>
                    
                    {ticket?.benefits && ticket?.benefits?.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Icon name="Star" size={14} className="text-accent" />
                        <span className="text-muted-foreground">
                          {ticket?.benefits?.length} benefit{ticket?.benefits?.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Benefits List */}
                  {ticket?.benefits && ticket?.benefits?.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {ticket?.benefits?.map((benefit, index) => (
                        <div key={index} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Icon name="Check" size={12} className="text-success" />
                          <span>{benefit}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${
                      isDisabled ? 'text-muted-foreground' : 'text-foreground'
                    }`}>
                      {formatPrice(ticket?.price)}
                    </div>
                    {ticket?.originalPrice && ticket?.originalPrice > ticket?.price && (
                      <div className="text-sm text-muted-foreground line-through">
                        {formatPrice(ticket?.originalPrice)}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {cartQuantity > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 rounded-lg">
                        <Button
                          variant="ghost"
                          size="xs"
                          iconName="Minus"
                          onClick={() => onAddToCart(ticket, -1)}
                          className="w-6 h-6 p-0"
                        />
                        <span className="font-medium text-primary min-w-[20px] text-center">
                          {cartQuantity}
                        </span>
                        <Button
                          variant="ghost"
                          size="xs"
                          iconName="Plus"
                          onClick={() => onAddToCart(ticket, 1)}
                          className="w-6 h-6 p-0"
                          disabled={cartQuantity >= 8}
                        />
                      </div>
                    )}
                    
                    {cartQuantity === 0 && (
                      <Button
                        variant={isDisabled ? "ghost" : "default"}
                        size="sm"
                        onClick={() => onAddToCart(ticket, 1)}
                        disabled={isDisabled}
                        iconName="Plus"
                        iconPosition="left"
                      >
                        {isDisabled ? 'Sold Out' : 'Add'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Ticket Policies */}
      <div className="mt-6 pt-6 border-t border-border">
        <h4 className="font-medium text-foreground mb-3">Ticket Information</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Icon name="Smartphone" size={16} />
            <span>Mobile tickets available</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="RefreshCw" size={16} />
            <span>Free cancellation until 24h before</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Shield" size={16} />
            <span>Secure payment processing</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={16} />
            <span>Instant ticket delivery</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketTiers;